import { useMemo, useState } from 'react';

import Constants from 'constants/constants';
import moment from 'moment-timezone';
import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';
import { WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useMutationExtractDelayDriverCsv } from 'api/mutations/extractDelayDriverCsv.mutation';
import { useQueryActivities } from 'api/queries/getDelayDriver.query';

import {
    FilterNames,
    filterMapper,
    getFiltersData,
} from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';

export const useCriticalActivitiesAndDelayDriversProvider = ({
    widgetId,
    contractId,
    projectId,
    latestVersionId,
    globalFilterData,
    localFilters,
    table,
    componentKey,
    widgetViewMode,
    endpoints,
}) => {
    const isInPinboard = Boolean(widgetId);
    const { isGenerativeAi, reportId } = usePinboardWidgetWrapperContext();

    //local state
    const [dateFilter, setDateFilter] = useState<ISelectOption<string> | null>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.dateFilter : null,
    );
    const [isCriticalPath, setIsCriticalPath] = useState<boolean>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.isCriticalPath : false,
    );
    const [isDelayDriver, setIsDelayDriver] = useState<boolean>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.isDelayDriver : true,
    );
    const [typeFilter, setTypeFilter] = useState<ISelectOption<string>[]>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? (localFilters?.typeFilter ?? []) : [],
    );
    const [onSearch, setOnSearch] = useState<string>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? (localFilters?.typeFilter ?? '') : '',
    );
    const [statusFilter, setStatusFilter] = useState<ISelectOption<string>[]>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode)
            ? localFilters?.statusFilter
            : [
                  {
                      value: 'TODO',
                      label: 'To Do',
                  },
                  {
                      value: 'IN_PROGRESS',
                      label: 'In Progress',
                  },
              ],
    );

    const source = isInPinboard
        ? localFilters
        : {
              ...globalFilterData,
              dateFilter,
              onSearch,
              statusFilter,
              isCriticalPath,
              typeFilter,
              isDelayDriver,
          };

    const filters = useMemo(
        () =>
            getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item.isExclude)
                    .map((i) => i.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item.isExclude)
                    .map((i) => i.name),
                [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
                [FilterNames.TAG_CARD_BOARD]: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
                [FilterNames.WBS_SEARCH]: source?.wbs,
                [FilterNames['START_DATE']]:
                    source?.dateFilter && source?.dateFilter.value !== 'All Months' ? [source?.dateFilter.value] : [],
                [FilterNames.NAME_SEARCH]: source?.onSearch?.length > 0 ? [source?.onSearch] : [],
                [FilterNames.OPTIONS_STATUS]: source?.statusFilter?.map((item) => item.value)?.map(filterMapper),
                [FilterNames.OPTIONS_TYPE]: source?.isCriticalPath
                    ? [...(source?.typeFilter ?? []).map((item) => filterMapper(item.value)), 'CRITICAL_PATH']
                    : source?.typeFilter?.map((item) => item.value)?.map(filterMapper),
                [FilterNames.DELAY_DRIVER]: source?.isDelayDriver ? ['true'] : ['false'],
            }),
        [
            source?.activityCodes,
            source?.milestoneFragnet,
            source?.tags,
            source?.wbs,
            source?.dateFilter,
            source?.typeFilter,
            source?.statusFilter,
            source?.onSearch,
            source?.isCriticalPath,
            source?.isDelayDriver,
        ],
    );

    return {
        ui: {
            state: getState({ latestVersionId, table, source, projectId, contractId }),
            setters: {
                setDateFilter,
                setStatusFilter,
                setIsCriticalPath,
                setIsDelayDriver,
                setTypeFilter,
                setOnSearch,
            },
            pills: useGetPills(source, widgetId, latestVersionId),
            isInPinboard,
        },
        api: {
            getDelayedDrivers: {
                instance: useQueryActivities(
                    getDelayedActivitiesPayload({
                        latestVersionId,
                        table,
                        filters,
                        isGenerativeAi,
                        componentKey,
                        widgetId,
                        reportId,
                    }),
                    endpoints.getDelayDriver,
                ),
                payload: getDelayedActivitiesPayload({
                    latestVersionId,
                    table,
                    filters,
                    isGenerativeAi,
                    componentKey,
                    widgetId,
                    reportId,
                }),
                endpoint: endpoints.getDelayDriver,
            },
            extractDelayDriverCsv: {
                instance: useMutationExtractDelayDriverCsv(),
                payload: getCsvExportMutationPayload({ latestVersionId, filters }),
            },
        },
    };
};

const getState = ({ projectId, contractId, latestVersionId, table, source }) => {
    return {
        // project
        projectId,
        contractId,
        latestVersionId,

        // global filter
        activityCodes: source?.activityCodes,
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags,
        wbs: source?.wbs,

        // table
        sorting: table.sorting,
        pagination: table.pagination,

        // local
        onSearch: source?.onSearch,
        typeFilter: source?.typeFilter,
        statusFilter: source?.statusFilter,
        isDelayDriver: source?.isDelayDriver,
        isCriticalPath: source?.isCriticalPath,
        dateFilter: source?.dateFilter,
    };
};

const useGetPills = (source, widgetId, latestVersionId) => {
    const pillsData = {
        activityCodes: source?.activityCodes?.filter((item) => !item?.isExclude).map((i) => i.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item?.isExclude).map((i) => i?.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item?.isExclude).map((i) => i?.name),
        tagsExclude: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
        wbs: source?.wbs,
        statusFilter: source?.statusFilter?.map((item) => item.label),
        onSearch: source?.onSearch,
        isDelayDriver: source?.isDelayDriver ? ['true'] : ['false'],
        dateFilter:
            source?.dateFilter && source?.dateFilter.value !== 'All Months'
                ? moment(source?.dateFilter.value).format(Constants.formats.date.monthAndYear)
                : [],
        typeFilter: source?.isCriticalPath
            ? [...(source?.typeFilter ?? []).map((item) => item.value), 'Critical Path']
            : source?.typeFilter?.map((item) => item.value),
    };
    const { pillsList: pills } = useGlobalFiltersHook({ widgetId, source: pillsData, latestVersionId });

    return pills;
};

const getDelayedActivitiesPayload = ({
    filters,
    latestVersionId,
    table,
    isGenerativeAi,
    componentKey,
    widgetId,
    reportId,
}) => {
    return {
        filter: {
            filters,
        },
        id: [latestVersionId],
        sortingRequestList: table.sorting,
        paging: { ...table.pagination, page: table.pagination.page - 1 },
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId, reportId),
    };
};

const getCsvExportMutationPayload = ({ latestVersionId, filters }) => {
    return {
        fileMetaDataId: latestVersionId,
        filter: filters,
    };
};
